import { Crepe } from '@milkdown/crepe';
import { insert } from '@milkdown/kit/utils';
import { Milkdown, MilkdownProvider, useEditor } from '@milkdown/react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export interface MilkdownEditorHandle {
  getMarkdown: () => string;
  resetContent: (markdown: string) => void;
  /** Insert markdown at the current cursor position. No-op if editor not ready. */
  insertMarkdownAtCursor: (markdown: string) => void;
}

/** Called when the user pastes files. Insert each file's markdown via the insert callback. */
export type OnFilePaste = (
  files: File[],
  insert: (markdown: string) => void,
) => Promise<void>;

interface MilkdownEditorProps {
  /** Initial content (used only when the editor is created or reset). */
  value?: string;
  /** Handler for image uploads (drag-drop, paste, or button). Returns the URL. */
  onUpload?: (file: File) => Promise<string>;
  /** Handler for pasted files (images → embed, others → link). Runs in capture phase. */
  onFilePaste?: OnFilePaste;
}

/** Mermaid preview for Crepe code-block (same behavior as before). */
function mermaidRenderPreview(
  language: string,
  content: string,
  applyPreview: (element: HTMLElement) => void,
): HTMLElement | null {
  if (language !== 'mermaid' || !content.trim()) return null;

  const container = document.createElement('div');
  container.style.display = 'flex';
  container.style.justifyContent = 'center';
  container.style.padding = '1rem';
  container.style.minHeight = '2rem';
  container.textContent = 'Rendering diagram...';

  import('mermaid')
    .then(({ default: mermaid }) => {
      try {
        // Reset Mermaid state to avoid stale cached diagrams after save/edit
        mermaid.mermaidAPI?.reset?.();
        // Use SVG <text>/<tspan> instead of <foreignObject> so Milkdown's DOMPurify
        // doesn't strip labels: read-only uses dangerouslySetInnerHTML (no sanitize),
        // editor preview uses DOMPurify.sanitize() which strips foreignObject.
        // Top-level htmlLabels + flowchart.htmlLabels ensure kanban headers and entries
        // both use SVG text (insertNode/insertCluster read from both config paths).
        mermaid.initialize({
          startOnLoad: false,
          theme: 'default',
          securityLevel: 'loose',
          htmlLabels: false,
          flowchart: { htmlLabels: false },
        });
        const id = `mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        mermaid
          .render(id, content)
          .then(({ svg: svgCode }) => {
            // DOMPurify (in Milkdown's preview panel) strips <style> from SVG.
            // Extract Mermaid's <style> and inject into document.head so text/fill apply.
            const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
            let match;
            const styleParts: string[] = [];
            while ((match = styleRegex.exec(svgCode)) !== null) {
              styleParts.push(match[1]);
            }
            const cleanedSvg =
              styleParts.length > 0
                ? svgCode.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                : svgCode;
            if (styleParts.length > 0) {
              const styleEl = document.createElement('style');
              styleEl.setAttribute('data-mermaid-preview', id);
              styleEl.textContent = styleParts.join('\n');
              document.head.appendChild(styleEl);
              document
                .querySelectorAll('style[data-mermaid-preview]')
                .forEach((el) => {
                  if (el !== styleEl) el.remove();
                });
            }

            const wrapper = document.createElement('div');
            wrapper.style.display = 'flex';
            wrapper.style.justifyContent = 'center';
            wrapper.innerHTML = cleanedSvg;
            applyPreview(wrapper);
          })
          .catch((err: unknown) => {
            const message =
              err instanceof Error ? err.message : 'Failed to render diagram';
            container.textContent = `Mermaid Error: ${message}`;
            container.style.color = '#dc2626';
            applyPreview(container);
          });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to render diagram';
        container.textContent = `Mermaid Error: ${message}`;
        container.style.color = '#dc2626';
        applyPreview(container);
      }
    })
    .catch((err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'Failed to load mermaid';
      container.textContent = `Mermaid Error: ${message}`;
      container.style.color = '#dc2626';
      applyPreview(container);
    });

  return container;
}

const MilkdownEditorInner = forwardRef<
  MilkdownEditorHandle,
  MilkdownEditorProps
>(function MilkdownEditorInner({ value, onUpload, onFilePaste }, forwardedRef) {
  const [content, setContent] = useState(() => value ?? '');
  const [resetTrigger, setResetTrigger] = useState(0);
  const crepeRef = useRef<Crepe | null>(null);

  useEditor(
    (root) => {
      const crepe = new Crepe({
        root,
        defaultValue: content,
        featureConfigs: {
          'code-mirror': {
            renderPreview: mermaidRenderPreview,
            previewLoading: 'Rendering diagram...',
            previewOnlyByDefault: true,
          },
          ...(onUpload && {
            'image-block': {
              onUpload,
            },
          }),
        },
      });
      crepeRef.current = crepe;
      return crepe;
    },
    [resetTrigger, content, onUpload],
  );

  useImperativeHandle(
    forwardedRef,
    () => ({
      getMarkdown: () => crepeRef.current?.getMarkdown() ?? '',
      resetContent: (markdown: string) => {
        setContent(markdown);
        setResetTrigger((t) => t + 1);
      },
      insertMarkdownAtCursor: (markdown: string) => {
        const crepe = crepeRef.current;
        const editor = crepe?.editor;
        if (editor) {
          try {
            editor.action(insert(markdown));
          } catch {
            // Editor may not be fully ready
          }
        }
      },
    }),
    [],
  );

  const pasteRef = useRef<HTMLDivElement>(null);
  const handlePasteCapture = useCallback(
    (e: ClipboardEvent) => {
      const files = e.clipboardData?.files;
      if (
        !files?.length ||
        !onFilePaste ||
        !pasteRef.current?.contains(e.target as Node)
      )
        return;
      e.preventDefault();
      e.stopPropagation();
      const insertFn = (markdown: string) => {
        const crepe = crepeRef.current;
        const editor = crepe?.editor;
        if (editor) {
          try {
            editor.action(insert(markdown));
          } catch {
            // Editor may not be fully ready
          }
        }
      };
      onFilePaste(Array.from(files), insertFn).catch(console.error);
    },
    [onFilePaste],
  );

  useEffect(() => {
    const el = pasteRef.current;
    if (!el || !onFilePaste) return;
    el.addEventListener('paste', handlePasteCapture, true);
    return () => el.removeEventListener('paste', handlePasteCapture, true);
  }, [onFilePaste, handlePasteCapture]);

  return (
    <div
      ref={pasteRef}
      className="milkdown-editor-root"
      style={{ height: '100%' }}
    >
      <Milkdown />
    </div>
  );
});

const MilkdownEditor = forwardRef<MilkdownEditorHandle, MilkdownEditorProps>(
  (props, ref) => (
    <MilkdownProvider>
      <MilkdownEditorInner {...props} ref={ref} />
    </MilkdownProvider>
  ),
);

MilkdownEditor.displayName = 'MilkdownEditor';

interface EditDocumentEditorProps {
  value?: string;
  /** Handler for image uploads (drag-drop, paste, or button). Returns the URL. */
  onUpload?: (file: File) => Promise<string>;
  /** Handler for pasted files (images → embed, others → link). */
  onFilePaste?: OnFilePaste;
}

export const EditDocumentEditor = forwardRef<
  MilkdownEditorHandle,
  EditDocumentEditorProps
>(function EditDocumentEditor({ value, onUpload, onFilePaste }, ref) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div style={{ flex: 1, minHeight: 0 }}>
        <MilkdownEditor
          ref={ref}
          value={value ?? ''}
          onUpload={onUpload}
          onFilePaste={onFilePaste}
        />
      </div>
    </div>
  );
});
